import React, { useEffect, memo } from 'react';
import { Text } from '@cuemath/web-comps';
import { Spinner } from '@cuemath/comps';

import { useFunnelBlog, useFunnelMonthBlog } from '../api/get-funnel';
import KpiCards from '../components/kpi-cards';
import PeriodChips from '../components/period-chips';
import TopSeriesGraphCard from '../components/top-series-graph';
import FunnelTable, { buildContributorColumns } from '../components/funnel-table';
import { SectionLabel, StateBlock, LoadingOverlay, UI } from '../styled-comps';
import {
  CHANNELS,
  METRICS,
  METRIC_LABELS,
  EMPTY_MESSAGE,
  TOP_PAGES_COUNT,
  UNKNOWN_REASONS,
} from '../constants';
import {
  asRows,
  metricFields,
  sumRows,
  channelLabel,
  buildRangeParams,
  buildMonthlyTopSeries,
} from '../helpers';
import { useSettledKey } from '../hooks/use-settled-key';
import { usePeriodSelection } from '../hooks/use-period-selection';
import { useGraphControls } from '../hooks/use-graph-controls';

// Blogs tab — same chip layout as Brand/Intent/Content, broken down by the
// actual BLOG the lead came through. The blog is read from the lead's tracking
// record: blog referrals stamp meta.utm_params with utm_source='blog-lead' and
// the blog is the raw utm_medium, shown verbatim (leads without the blog-lead
// tag bucket as '(unknown)'). utm_campaign is deliberately NOT used — it lumps
// many articles under a few slugs (e.g. 'blog-homepage',
// 'impactstories-studentstories'). The page-URL columns can't be used — they get
// overwritten by the signup-flow pages before the lead lands.
//   * Year + month + region + user-type chips -> KPI cards + a table of every
//     blog, ranked by QL (rows sum to the card totals).
//   * Independent graph section: top blogs of the selected year, month-on-month.
const Blogs = memo(() => {
  const channel = CHANNELS.BLOG;

  // ── table/cards section state: month-or-range period + region/user-type ──
  const { periodProps, window: tableWindow, label: periodLabel, regions, countries, userTypes } =
    usePeriodSelection();

  // ── graph section state (fully independent, its own Apply) ──
  const {
    graphProps,
    metric: graphMetric,
    appliedYear: graphYear,
    appliedRegions: graphRegions,
    appliedCountries: graphCountries,
    appliedUserTypes: graphUserTypes,
  } = useGraphControls();

  const {
    data: blogData,
    get: getBlog,
    isProcessing: blogLoading,
    error: blogError,
  } = useFunnelBlog();
  const {
    data: monthBlogData,
    get: getMonthBlog,
    isProcessing: graphLoading,
    error: graphError,
  } = useFunnelMonthBlog();

  const tableQuery = buildRangeParams(tableWindow.start, tableWindow.end, {
    channel,
    regions,
    countries,
    userTypes,
  });
  const tableKey = JSON.stringify(tableQuery);
  useEffect(() => {
    getBlog('wm-blog', tableQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  const graphQuery = buildRangeParams(
    new Date(graphYear, 0, 1),
    new Date(graphYear, 11, 31),
    { channel, regions: graphRegions, countries: graphCountries, userTypes: graphUserTypes },
  );
  const graphKey = JSON.stringify(graphQuery);
  useEffect(() => {
    getMonthBlog('wm-month-blog', graphQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphKey]);

  // True only once `blogData` reflects the applied period — so the table/cards
  // never render the previous period's rows under the freshly-changed heading.
  const fresh = useSettledKey(tableKey, blogLoading, blogError);

  // ── table/cards: one row per blog ──
  const blogRows = (fresh ? asRows(blogData) : [])
    .map(row => ({ label: row.blog, ...metricFields(row) }))
    .sort((a, b) => b[METRICS.QL] - a[METRICS.QL]);
  const totals = sumRows(blogRows);

  // ── graph: top blogs by QL across the WHOLE selected year ──
  const monthBlogRows = asRows(monthBlogData);
  const graphDatasets = buildMonthlyTopSeries(
    monthBlogRows,
    graphYear,
    graphMetric,
    row => row.blog,
  );

  const isFetching = blogLoading || graphLoading;

  return (
    <>
      {isFetching && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SectionLabel>{channelLabel(channel)} — pick a period</SectionLabel>
      <PeriodChips {...periodProps} />

      <SectionLabel>{`${channelLabel(channel)} — ${periodLabel}`}</SectionLabel>
      {blogError ? (
        <StateBlock>
          <Text variant="body1" color={UI.down}>{blogError}</Text>
        </StateBlock>
      ) : (blogLoading || !fresh) && !blogRows.length ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>Loading blog metrics…</Text>
        </StateBlock>
      ) : (
        <>
          <KpiCards totals={totals} compareTotals={null} />

          <SectionLabel>
            {`Blogs (${blogRows.length}) — ranked by QL; rows sum to the totals above`}
          </SectionLabel>
          <FunnelTable
            data={blogRows}
            columns={buildContributorColumns('Blog (utm_medium)', UNKNOWN_REASONS.BLOG)}
            loading={blogLoading}
            defaultSorted={[{ id: METRICS.QL, desc: true }]}
            emptyMessage={EMPTY_MESSAGE}
            showTotals
          />
        </>
      )}

      <TopSeriesGraphCard
        {...graphProps}
        sectionLabel={`Top ${TOP_PAGES_COUNT} blogs — month on month (ranked by QL over ${graphYear})`}
        caption={`Showing ${METRIC_LABELS[graphMetric]} per month for the top ${TOP_PAGES_COUNT} blogs of ${graphYear}. Chips here don't affect the table above.`}
        datasets={graphDatasets}
        hasRawData={!!monthBlogRows.length}
        loading={graphLoading}
        error={graphError}
      />
    </>
  );
});

export default Blogs;
